import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { HeatPlan } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import { InputInt, ValidatedInt, DataSourceLookup, InputNullableDateTime, DateComp, InputBool, DisplayBool } from '@/components';
import { useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { type DateType } from '@/utilities/dateutils';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class HeatPlanGuiDef extends GuiDef<HeatPlan> {
    constructor() {
        super('HeatPlan');

        const EventCommon = storeToRefs(useEventCommonStore());
        const RacesSource = computed(() => {
            return EventCommon.RacesAsSelectLine.value;
        });

        const SortOrder = new FieldDef<HeatPlan>();
        SortOrder.Name = 'SortOrder';
        SortOrder.FieldName = 'SortOrder';
        SortOrder.DefinedFor = 'Both';
        SortOrder.TranslationKey = 'heatplan.sortorder';
        SortOrder.Editable = true;
        SortOrder.Filterable = false;
        SortOrder.Sortable = true;
        SortOrder.CssClasses = '';
        SortOrder.value = (heatplan) => heatplan.SortOrder;
        SortOrder.setValue = (heatplan, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            heatplan.SortOrder = value;
        };
        SortOrder.DetailEditor = markRaw(InputInt);
        SortOrder.DetailEditorProps = {};
        SortOrder.CellEditor = markRaw(InputInt);
        SortOrder.CellEditorProps = {};
        SortOrder.CellRenderer = markRaw(ValidatedInt);
        SortOrder.CellRendererProps = {};
        SortOrder.Validator = null;
        SortOrder.SortDirection = undefined;
        SortOrder.cellClassRules = undefined;
        SortOrder.MinWidth = undefined;
        SortOrder.MaxWidth = undefined;
        SortOrder.Tooltip = null;
        this.Fields.push(SortOrder);

        const RaceID = new FieldDef<HeatPlan>();
        RaceID.Name = 'RaceID';
        RaceID.FieldName = 'RaceID';
        RaceID.DefinedFor = 'Both';
        RaceID.TranslationKey = 'heatplan.raceid';
        RaceID.Editable = false;
        RaceID.Filterable = false;
        RaceID.Sortable = true;
        RaceID.CssClasses = '';
        RaceID.value = (heatplan) => heatplan.RaceID;
        RaceID.setValue = () => {};
        // @ts-expect-error @ts-ignore
        RaceID.CellRenderer = markRaw(DataSourceLookup);
        RaceID.CellRendererProps = { datasource: { mapsource: RacesSource }, readonly: true, hideCaret: true };
        RaceID.ExportValueFormatter = (obj: HeatPlan) => {
            return RacesSource.value.get(obj.RaceID)?.text;
        };
        RaceID.Validator = null;
        RaceID.SortDirection = undefined;
        RaceID.cellClassRules = undefined;
        RaceID.MinWidth = undefined;
        RaceID.MaxWidth = undefined;
        RaceID.Tooltip = null;
        this.Fields.push(RaceID);

        const ScheduledStarttime = new FieldDef<HeatPlan>();
        ScheduledStarttime.Name = 'ScheduledStarttime';
        ScheduledStarttime.FieldName = 'ScheduledStarttime';
        ScheduledStarttime.DefinedFor = 'Both';
        ScheduledStarttime.TranslationKey = 'heatplan.scheduledstarttime';
        ScheduledStarttime.Editable = true;
        ScheduledStarttime.Filterable = true;
        ScheduledStarttime.Sortable = true;
        ScheduledStarttime.CssClasses = '';
        ScheduledStarttime.value = (heatplan) => heatplan.ScheduledStarttime;
        ScheduledStarttime.setValue = (heatplan, value) => (heatplan.ScheduledStarttime = value);
        ScheduledStarttime.DetailEditor = markRaw(InputNullableDateTime);
        ScheduledStarttime.DetailEditorProps = { format: 'TIME' as DateType };
        ScheduledStarttime.CellEditor = markRaw(InputNullableDateTime);
        ScheduledStarttime.CellEditorProps = { format: 'TIME' as DateType };
        ScheduledStarttime.CellRenderer = markRaw(DateComp);
        ScheduledStarttime.CellRendererProps = { format: 'TIME' as DateType };
        ScheduledStarttime.Validator = null;
        ScheduledStarttime.SortDirection = undefined;
        ScheduledStarttime.cellClassRules = undefined;
        ScheduledStarttime.MinWidth = 180;
        ScheduledStarttime.MaxWidth = undefined;
        ScheduledStarttime.Tooltip = null;
        this.Fields.push(ScheduledStarttime);

        const Started = new FieldDef<HeatPlan>();
        Started.Name = 'Started';
        Started.FieldName = 'Started';
        Started.DefinedFor = 'Both';
        Started.TranslationKey = 'heatplan.started';
        Started.Editable = false;
        Started.Filterable = false;
        Started.Sortable = true;
        Started.CssClasses = '';
        Started.value = (heatplan) => heatplan.Started;
        Started.setValue = () => {};
        Started.DetailEditor = markRaw(InputBool);
        Started.DetailEditorProps = { label: 'heatplan.started', readonly: true };
        Started.CellEditor = markRaw(InputBool);
        Started.CellEditorProps = {};
        Started.CellRenderer = markRaw(DisplayBool);
        Started.CellRendererProps = {};
        Started.Validator = null;
        Started.SortDirection = undefined;
        Started.cellClassRules = undefined;
        Started.MinWidth = undefined;
        Started.MaxWidth = undefined;
        Started.Tooltip = null;
        this.Fields.push(Started);

        const Starttime = new FieldDef<HeatPlan>();
        Starttime.Name = 'Starttime';
        Starttime.FieldName = 'Starttime';
        Starttime.DefinedFor = 'Both';
        Starttime.TranslationKey = 'heatplan.starttime';
        Starttime.Editable = false;
        Starttime.Filterable = true;
        Starttime.Sortable = true;
        Starttime.CssClasses = '';
        Starttime.value = (heatplan) => heatplan.Starttime;
        Starttime.setValue = () => {};
        Starttime.DetailEditor = markRaw(InputNullableDateTime);
        Starttime.DetailEditorProps = { format: 'TIME' as DateType, readonly: true };
        Starttime.CellEditor = markRaw(InputNullableDateTime);
        Starttime.CellEditorProps = { format: 'TIME' as DateType, readonly: true };
        Starttime.CellRenderer = markRaw(DateComp);
        Starttime.CellRendererProps = { format: 'TIME' as DateType, readonly: true };
        Starttime.Validator = null;
        Starttime.SortDirection = undefined;
        Starttime.cellClassRules = undefined;
        Starttime.MinWidth = 180;
        Starttime.MaxWidth = undefined;
        Starttime.Tooltip = null;
        this.Fields.push(Starttime);

        const Finished = new FieldDef<HeatPlan>();
        Finished.Name = 'Finished';
        Finished.FieldName = 'Finished';
        Finished.DefinedFor = 'Both';
        Finished.TranslationKey = 'heatplan.finished';
        Finished.Editable = false;
        Finished.Filterable = false;
        Finished.Sortable = true;
        Finished.CssClasses = '';
        Finished.value = (heatplan) => heatplan.Finished;
        Finished.setValue = () => {};
        Finished.DetailEditor = markRaw(InputBool);
        Finished.DetailEditorProps = { label: 'heatplan.finished', readonly: true };
        Finished.CellEditor = markRaw(InputBool);
        Finished.CellEditorProps = {};
        Finished.CellRenderer = markRaw(DisplayBool);
        Finished.CellRendererProps = {};
        Finished.Validator = null;
        Finished.SortDirection = undefined;
        Finished.cellClassRules = undefined;
        Finished.MinWidth = undefined;
        Finished.MaxWidth = undefined;
        Finished.Tooltip = null;
        this.Fields.push(Finished);
    }

    public async LoadDependencies() {
        await useEventCommonStore().EnsureLoaded(['Races']);
    }
}
